import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiPlus,
  HiPencil,
  HiTrash,
  HiEye,
  HiXMark,
  HiCheck,
  HiExclamationTriangle,
  HiUserGroup,
  HiAcademicCap,
  HiPhone,
  HiMail,
  HiStar,
  HiClock,
  HiShieldCheck,
  HiMagnifyingGlass,
  HiFunnel,
  HiArrowPath,
  HiUser,
  HiEnvelope,
  HiBuildingOffice2,
  HiCheckCircle,
  HiLockClosed,
} from 'react-icons/hi2';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  query,
  orderBy,
  where,
  serverTimestamp,
  setDoc,
  getDoc,
} from 'firebase/firestore';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { firestore } from '../../database/firebase';
import ConfirmationModal from '../SharedFeature/ConfirmationModal';

const Toast = ({ message, type, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: -50, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -50, scale: 0.95 }}
    className={`fixed right-4 top-4 z-50 flex items-center gap-3 rounded-xl px-4 py-3 shadow-lg ${
      type === 'success'
        ? 'border border-green-200 bg-green-50'
        : type === 'error'
          ? 'border border-red-200 bg-red-50'
          : 'border border-gray-200 bg-gray-50'
    }`}
  >
    {type === 'success' && <HiCheckCircle className="h-5 w-5 text-green-600" />}
    {type === 'error' && <HiExclamationTriangle className="h-5 w-5 text-red-600" />}
    <span
      className={`text-sm font-medium ${type === 'success' ? 'text-green-800' : type === 'error' ? 'text-red-800' : 'text-black'}`}
    >
      {message}
    </span>
    <button
      onClick={onClose}
      className={`ml-2 hover:opacity-70 ${type === 'success' ? 'text-green-600' : type === 'error' ? 'text-red-600' : 'text-gray-600'}`}
    >
      <HiXMark className="h-4 w-4" />
    </button>
  </motion.div>
);

const CoachesForms = () => {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCoach, setEditingCoach] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [validationErrors, setValidationErrors] = useState({});
  const [clientsForCoach, setClientsForCoach] = useState([]);
  const [viewCoach, setViewCoach] = useState(null);
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, coach: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    location: '',
    organization: '',
    phone: '',
    role: 'Mentor',
    specialization: '',
    status: 'Active',
  });

  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  const loadCoaches = async () => {
    try {
      setLoading(true);
      showToast('Loading coaches...', 'info');
      const coachesRef = collection(firestore, 'coaches');
      let querySnapshot;
      try {
        const q = query(coachesRef, orderBy('email', 'asc'));
        querySnapshot = await getDocs(q);
      } catch (error) {
        showToast('Ordering by email failed, trying without ordering: ' + error.message, 'error');
        querySnapshot = await getDocs(coachesRef);
      }
      const coachesData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        let status = data.status || 'Active';
        if (typeof status === 'string') {
          status = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
        }
        return {
          id: doc.id,
          firstname: data.firstname || '',
          lastname: data.lastname || '',
          email: data.email || '',
          location: data.location || '',
          organization: data.organization || '',
          phone: data.phone || '',
          role: data.role || 'Mentor',
          specialization: data.specialization || '',
          status,
          createdAt: data.createdAt || null,
          updatedAt: data.updatedAt || null,
          uid: data.uid || '',
          ...data,
        };
      });
      coachesData.sort((a, b) => a.email.localeCompare(b.email));
      setCoaches(coachesData);
      if (coachesData.length === 0) {
        showToast('No coaches found in database', 'info');
      } else {
        showToast(`Loaded ${coachesData.length} coaches successfully`, 'success');
      }
    } catch (error) {
      showToast(`Failed to load coaches: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCoaches();
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!formData.firstname.trim()) errors.firstname = 'First name is required';
    if (!formData.lastname.trim()) errors.lastname = 'Last name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      errors.email = 'Please enter a valid email address';
    if (!formData.phone.trim()) errors.phone = 'Phone number is required';
    else if (!/^\+?[\d\s\-\(\)]+$/.test(formData.phone))
      errors.phone = 'Please enter a valid phone number';
    if (!formData.specialization.trim()) errors.specialization = 'Specialization is required';
    if (!formData.location.trim()) errors.location = 'Location is required';
    if (!formData.organization.trim()) errors.organization = 'Organization is required';
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({
      firstname: '',
      lastname: '',
      email: '',
      location: '',
      organization: '',
      phone: '',
      role: 'Mentor',
      specialization: '',
      status: 'Active',
    });
    setValidationErrors({});
    setEditingCoach(null);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);

      if (editingCoach) {
        const coachRef = doc(firestore, 'coaches', editingCoach.id);
        const updateData = {
          ...formData,
          updatedAt: serverTimestamp(),
        };
        await updateDoc(coachRef, updateData);
        showToast('Coach updated successfully!', 'success');
      } else {
        const defaultPassword = 'Password@12345';
        const tempApp = initializeApp(
          {
            apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
            authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
            projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
            storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
            messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
            appId: process.env.REACT_APP_FIREBASE_APP_ID,
          },
          'temp-app-' + Date.now()
        );

        const tempAuth = getAuth(tempApp);

        const userCredential = await createUserWithEmailAndPassword(
          tempAuth,
          formData.email,
          defaultPassword
        );

        const coachData = {
          ...formData,
          uid: userCredential.user.uid,
          id: userCredential.user.uid,
          name: `${formData.firstname} ${formData.lastname}`,
          role: formData.role,
          status: 'active',
          createdAt: new Date(),
        };

        await setDoc(doc(firestore, 'coaches', userCredential.user.uid), coachData);
        await tempAuth.signOut();

        showToast(`Coach created successfully! Password: ${defaultPassword}`, 'success');
      }
      setShowModal(false);
      resetForm();
      await loadCoaches();
    } catch (error) {
      let errorMessage = 'Failed to save coach';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'A user with this email already exists';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak';
      } else if (error.message) {
        errorMessage = error.message;
      }

      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = coach => {
    setEditingCoach(coach);
    setFormData({
      firstname: coach.firstname || '',
      lastname: coach.lastname || '',
      email: coach.email || '',
      location: coach.location || '',
      organization: coach.organization || '',
      phone: coach.phone || '',
      role: coach.role || 'Mentor',
      specialization: coach.specialization || '',
      status: coach.status || 'Active',
    });
    setShowModal(true);
  };

  const handleDeleteClick = coach => {
    setDeleteConfirm({ isOpen: true, coach });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.coach) return;

    setIsDeleting(true);
    try {
      // Get the full coach data
      const coachDoc = await getDoc(doc(firestore, 'coaches', deleteConfirm.coach.id));
      const coachData = coachDoc.data();

      // Add deletion metadata
      const deletedUserData = {
        ...coachData,
        originalCollection: 'coaches',
        deletedAt: new Date().toISOString(),
        deletedBy: 'admin',
      };

      // Move to deleted_users collection
      await setDoc(doc(firestore, 'deleted_users', deleteConfirm.coach.id), deletedUserData);

      // Delete from coaches collection
      await deleteDoc(doc(firestore, 'coaches', deleteConfirm.coach.id));
      showToast('Coach deleted and moved to deleted users!', 'success');
      setDeleteConfirm({ isOpen: false, coach: null });
      await loadCoaches();
    } catch (error) {
      showToast(`Failed to delete coach: ${error.message}`, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, coach: null });
  };

  const handleView = async coach => {
    setViewCoach(coach);

    if (coach?.email) {
      try {
        const q = query(
          collection(firestore, 'clients'),
          where('assignedCoachEmail', '==', coach.email)
        );
        const snapshot = await getDocs(q);
        const clients = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setClientsForCoach(clients);
      } catch (error) {
        showToast('Error loading clients for coach: ' + error.message, 'error');
        setClientsForCoach([]);
      }
    } else {
      setClientsForCoach([]);
    }
  };

  const totalCoaches = coaches.filter(c => c?.role === 'Coach').length;
  const totalMentors = coaches.filter(c => c?.role === 'Mentor').length;
  const totalActive = coaches.filter(c => c?.status === 'Active').length;
  const totalInactive = coaches.filter(c => c?.status === 'Inactive').length;

  const filteredCoaches = coaches.filter(coach => {
    const matchesSearch =
      searchTerm === '' ||
      `${coach.firstname} ${coach.lastname}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coach.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coach.organization?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || coach.role === filterRole;
    const matchesStatus = filterStatus === 'all' || coach.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 p-4 md:p-6 lg:p-8">
      <div className="mx-auto max-w-7xl">
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="space-y-2">
              <h1 className="text-3xl font-bold tracking-tight text-black md:text-4xl">
                Coaches & Mentors
              </h1>
              <p className="text-sm text-gray-600 md:text-base">
                Manage and organize your coaching team
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-3 text-sm font-medium text-white shadow-lg shadow-blue-500/20 transition-all hover:bg-blue-700 hover:shadow-xl hover:shadow-blue-500/30 md:px-8 md:text-base"
            >
              <HiPlus className="h-5 w-5" />
              Add Coach/Mentor
            </motion.button>
          </div>
        </motion.div>

        <AnimatePresence>
          {toast && (
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          )}
        </AnimatePresence>

        <ConfirmationModal
          isOpen={deleteConfirm.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Coach"
          message={`Are you sure you want to delete ${deleteConfirm.coach?.firstname || ''} ${deleteConfirm.coach?.lastname || ''}? They will be moved to deleted users.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
          isLoading={isDeleting}
        />

        {/* Stats Cards */}
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Total Coaches', value: totalCoaches, icon: HiAcademicCap, color: 'blue' },
            { label: 'Total Mentors', value: totalMentors, icon: HiUserGroup, color: 'blue' },
            { label: 'Active', value: totalActive, icon: HiCheckCircle, color: 'green' },
            { label: 'Inactive', value: totalInactive, icon: HiClock, color: 'gray' },
          ].map((stat, idx) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1 }}
              className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-6 shadow-sm transition-all hover:border-gray-300 hover:shadow-md"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                  <p className="mt-2 text-3xl font-bold text-black">{stat.value}</p>
                </div>
                <div
                  className={`rounded-xl p-3 ${
                    stat.color === 'blue'
                      ? 'bg-blue-50'
                      : stat.color === 'green'
                        ? 'bg-green-50'
                        : 'bg-gray-50'
                  }`}
                >
                  <stat.icon
                    className={`h-6 w-6 ${
                      stat.color === 'blue'
                        ? 'text-blue-600'
                        : stat.color === 'green'
                          ? 'text-green-600'
                          : 'text-gray-600'
                    }`}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Search and Filters */}
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-1 flex-wrap items-center gap-3">
              <div className="relative min-w-[200px] flex-1">
                <HiMagnifyingGlass className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email, or organization..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-4 text-sm text-black placeholder-gray-400 transition-all focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
              <select
                value={filterRole}
                onChange={e => setFilterRole(e.target.value)}
                className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm font-medium text-black transition-all focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="all">All Roles</option>
                <option value="Coach">Coach</option>
                <option value="Mentor">Mentor</option>
              </select>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm font-medium text-black transition-all focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                <option value="all">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={loadCoaches}
              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-black transition-all hover:border-gray-300 hover:bg-gray-50"
            >
              <HiArrowPath className="h-4 w-4" />
              Refresh
            </motion.button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          {loading ? (
            <div className="flex flex-col items-center justify-center p-12">
              <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600"></div>
              <p className="mt-4 text-sm font-medium text-gray-600">Loading coaches...</p>
            </div>
          ) : filteredCoaches.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-12">
              <div className="rounded-full bg-gray-100 p-6">
                <HiUserGroup className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-black">No coaches found</h3>
              <p className="mt-2 text-sm text-gray-500">
                {coaches.length === 0
                  ? 'Get started by adding your first coach or mentor.'
                  : 'Try adjusting your search or filter criteria.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50/80">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                      <div className="flex items-center gap-2">
                        <HiUser className="h-4 w-4" />
                        Name
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                      <div className="flex items-center gap-2">
                        <HiEnvelope className="h-4 w-4" />
                        Email
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                      <div className="flex items-center gap-2">
                        <HiBuildingOffice2 className="h-4 w-4" />
                        Organization
                      </div>
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                      Role
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                      Specialization
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                      Status
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider text-gray-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {filteredCoaches.map((coach, idx) => (
                    <motion.tr
                      key={coach.email || idx}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.02 }}
                      className="group transition-colors hover:bg-gray-50"
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-sm font-semibold text-white shadow-sm">
                            {(coach.firstname || coach.email || 'C')[0].toUpperCase()}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-black">
                              {coach.firstname || ''} {coach.lastname || ''}
                            </div>
                            <div className="text-xs text-gray-500">{coach.phone || ''}</div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm text-black">{coach.email || '-'}</div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm text-black">{coach.organization || '-'}</div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span className="inline-flex items-center rounded-md bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                          {coach.role || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className="max-w-xs truncate text-sm text-black"
                          title={coach.specialization}
                        >
                          {coach.specialization || '-'}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <span
                          className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${
                            coach.status === 'Active'
                              ? 'border-green-200 bg-green-50 text-green-700'
                              : 'border-gray-200 bg-gray-50 text-gray-600'
                          }`}
                        >
                          {coach.status || 'Unknown'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleView(coach)}
                            className="rounded-lg p-2 text-gray-500 opacity-70 transition-all hover:bg-gray-100 hover:text-gray-700 group-hover:opacity-100"
                            title="View Details"
                          >
                            <HiEye className="h-4 w-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleEdit(coach)}
                            className="rounded-lg p-2 text-gray-500 opacity-70 transition-all hover:bg-amber-50 hover:text-amber-600 group-hover:opacity-100"
                            title="Edit Coach"
                          >
                            <HiPencil className="h-4 w-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleDeleteClick(coach)}
                            className="rounded-lg p-2 text-gray-500 opacity-70 transition-all hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                            title="Delete Coach"
                          >
                            <HiTrash className="h-4 w-4" />
                          </motion.button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Add/Edit Modal */}
        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
              onClick={() => setShowModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.95, opacity: 0, y: 20 }}
                className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl"
                onClick={e => e.stopPropagation()}
              >
                <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white px-6 py-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-600">
                        {editingCoach ? (
                          <HiPencil className="h-5 w-5 text-white" />
                        ) : (
                          <HiPlus className="h-5 w-5 text-white" />
                        )}
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-black">
                          {editingCoach ? 'Edit Coach/Mentor' : 'Add New Coach/Mentor'}
                        </h2>
                        <p className="text-sm text-gray-600">
                          {editingCoach
                            ? 'Update coach information'
                            : 'Create a new coach or mentor profile'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowModal(false)}
                      className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                    >
                      <HiXMark className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="max-h-[70vh] overflow-y-auto p-6">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Personal Information */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                      <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-700">
                        <HiUser className="h-4 w-4" />
                        Personal Information
                      </h3>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <label className="mb-1.5 block text-sm font-medium text-black">
                            First Name
                          </label>
                          <input
                            type="text"
                            value={formData.firstname}
                            onChange={e => setFormData({ ...formData, firstname: e.target.value })}
                            className={`w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-black placeholder-gray-400 transition-all focus:outline-none focus:ring-2 ${
                              validationErrors.firstname
                                ? 'border-red-300 focus:border-red-400 focus:ring-red-400/20'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20'
                            }`}
                            placeholder="Enter first name"
                          />
                          {validationErrors.firstname && (
                            <p className="mt-1.5 text-xs text-red-600">
                              {validationErrors.firstname}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="mb-1.5 block text-sm font-medium text-black">
                            Last Name
                          </label>
                          <input
                            type="text"
                            value={formData.lastname}
                            onChange={e => setFormData({ ...formData, lastname: e.target.value })}
                            className={`w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-black placeholder-gray-400 transition-all focus:outline-none focus:ring-2 ${
                              validationErrors.lastname
                                ? 'border-red-300 focus:border-red-400 focus:ring-red-400/20'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20'
                            }`}
                            placeholder="Enter last name"
                          />
                          {validationErrors.lastname && (
                            <p className="mt-1.5 text-xs text-red-600">
                              {validationErrors.lastname}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Contact Information */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                      <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-700">
                        <HiEnvelope className="h-4 w-4" />
                        Contact Information
                      </h3>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <label className="mb-1.5 block text-sm font-medium text-black">
                            Email Address
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                            disabled={!!editingCoach}
                            className={`w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-black placeholder-gray-400 transition-all focus:outline-none focus:ring-2 ${
                              validationErrors.email
                                ? 'border-red-300 focus:border-red-400 focus:ring-red-400/20'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20'
                            } ${editingCoach ? 'cursor-not-allowed bg-gray-100' : ''}`}
                            placeholder="coach@example.com"
                          />
                          {validationErrors.email && (
                            <p className="mt-1.5 text-xs text-red-600">{validationErrors.email}</p>
                          )}
                          {editingCoach && (
                            <p className="mt-1.5 text-xs text-gray-500">
                              Email cannot be changed after creation
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="mb-1.5 block text-sm font-medium text-black">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={e => setFormData({ ...formData, phone: e.target.value })}
                            className={`w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-black placeholder-gray-400 transition-all focus:outline-none focus:ring-2 ${
                              validationErrors.phone
                                ? 'border-red-300 focus:border-red-400 focus:ring-red-400/20'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20'
                            }`}
                            placeholder="+267 7XX XXX XXX"
                          />
                          {validationErrors.phone && (
                            <p className="mt-1.5 text-xs text-red-600">{validationErrors.phone}</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Professional Information */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-4">
                      <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-700">
                        <HiBuildingOffice2 className="h-4 w-4" />
                        Professional Information
                      </h3>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <label className="mb-1.5 block text-sm font-medium text-black">
                            Organization
                          </label>
                          <input
                            type="text"
                            value={formData.organization}
                            onChange={e =>
                              setFormData({ ...formData, organization: e.target.value })
                            }
                            className={`w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-black placeholder-gray-400 transition-all focus:outline-none focus:ring-2 ${
                              validationErrors.organization
                                ? 'border-red-300 focus:border-red-400 focus:ring-red-400/20'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20'
                            }`}
                            placeholder="Enter organization name"
                          />
                          {validationErrors.organization && (
                            <p className="mt-1.5 text-xs text-red-600">
                              {validationErrors.organization}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="mb-1.5 block text-sm font-medium text-black">
                            Location
                          </label>
                          <input
                            type="text"
                            value={formData.location}
                            onChange={e => setFormData({ ...formData, location: e.target.value })}
                            className={`w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-black placeholder-gray-400 transition-all focus:outline-none focus:ring-2 ${
                              validationErrors.location
                                ? 'border-red-300 focus:border-red-400 focus:ring-red-400/20'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20'
                            }`}
                            placeholder="City, Country"
                          />
                          {validationErrors.location && (
                            <p className="mt-1.5 text-xs text-red-600">
                              {validationErrors.location}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="mb-1.5 block text-sm font-medium text-black">
                            Role
                          </label>
                          <select
                            value={formData.role}
                            onChange={e => setFormData({ ...formData, role: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-black transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          >
                            <option value="Coach">Coach</option>
                            <option value="Mentor">Mentor</option>
                          </select>
                        </div>
                        <div>
                          <label className="mb-1.5 block text-sm font-medium text-black">
                            Status
                          </label>
                          <select
                            value={formData.status}
                            onChange={e => setFormData({ ...formData, status: e.target.value })}
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-black transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          >
                            <option value="Active">Active</option>
                            <option value="Inactive">Inactive</option>
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="mb-1.5 block text-sm font-medium text-black">
                            Specialization / Expertise
                          </label>
                          <textarea
                            value={formData.specialization}
                            onChange={e =>
                              setFormData({ ...formData, specialization: e.target.value })
                            }
                            rows={3}
                            className={`w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-black placeholder-gray-400 transition-all focus:outline-none focus:ring-2 ${
                              validationErrors.specialization
                                ? 'border-red-300 focus:border-red-400 focus:ring-red-400/20'
                                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500/20'
                            }`}
                            placeholder="e.g., Leadership, Finance, Marketing, Business Strategy"
                          />
                          {validationErrors.specialization && (
                            <p className="mt-1.5 text-xs text-red-600">
                              {validationErrors.specialization}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Auto-assigned Information - Only show for new coaches */}
                    {!editingCoach && (
                      <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-4">
                        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-700">
                          <HiShieldCheck className="h-4 w-4 text-blue-600" />
                          Auto-assigned Information
                        </h3>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          <div className="flex items-center gap-2 rounded-md bg-white px-3 py-2">
                            <HiLockClosed className="h-4 w-4 text-blue-600" />
                            <span className="text-xs text-gray-600">Default Password:</span>
                            <span className="rounded bg-gray-100 px-2 py-0.5 font-mono text-xs text-black">
                              Password@12345
                            </span>
                          </div>
                          <div className="flex items-center gap-2 rounded-md bg-white px-3 py-2">
                            <HiCheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-xs text-gray-600">Status:</span>
                            <span className="font-medium text-green-600">Active</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </form>
                </div>
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                  <div className="flex gap-3">
                    <motion.button
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => setShowModal(false)}
                      className="flex-1 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-all hover:border-gray-400 hover:bg-gray-50"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      disabled={loading}
                      className="flex-1 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:bg-blue-700 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {loading ? (
                        <div className="flex items-center justify-center gap-2">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          <span>{editingCoach ? 'Updating...' : 'Creating...'}</span>
                        </div>
                      ) : editingCoach ? (
                        'Update Coach/Mentor'
                      ) : (
                        'Create Coach/Mentor'
                      )}
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        {/* View Modal */}
        <AnimatePresence>
          {viewCoach && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
              onClick={() => setViewCoach(null)}
            >
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 20, scale: 0.95 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="relative max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
                onClick={e => e.stopPropagation()}
              >
                <div className="border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white px-6 py-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-lg font-bold text-white shadow-lg">
                        {(viewCoach.firstname ||
                          viewCoach.email ||
                          viewCoach.id)?.[0]?.toUpperCase() || 'C'}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-black">
                          {viewCoach.firstname || ''} {viewCoach.lastname || ''}
                        </h3>
                        <p className="text-sm text-gray-600">{viewCoach.email}</p>
                      </div>
                    </div>
                    <button
                      className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                      onClick={() => setViewCoach(null)}
                      aria-label="Close"
                    >
                      <HiXMark className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="max-h-[70vh] overflow-y-auto p-6">
                  <div className="space-y-6">
                    {/* Personal Info */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-5">
                      <h4 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-700">
                        <HiUser className="h-4 w-4" />
                        Personal Information
                      </h4>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <p className="text-xs font-medium text-gray-500">First Name</p>
                          <p className="mt-1 text-sm font-semibold text-black">
                            {viewCoach.firstname || '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Last Name</p>
                          <p className="mt-1 text-sm font-semibold text-black">
                            {viewCoach.lastname || '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Email</p>
                          <p className="mt-1 text-sm font-semibold text-black">
                            {viewCoach.email || '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Phone</p>
                          <p className="mt-1 text-sm font-semibold text-black">
                            {viewCoach.phone || '-'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Professional Info */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-5">
                      <h4 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-700">
                        <HiBuildingOffice2 className="h-4 w-4" />
                        Professional Information
                      </h4>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <p className="text-xs font-medium text-gray-500">Organization</p>
                          <p className="mt-1 text-sm font-semibold text-black">
                            {viewCoach.organization || '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Location</p>
                          <p className="mt-1 text-sm font-semibold text-black">
                            {viewCoach.location || '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Role</p>
                          <span className="mt-1 inline-flex rounded-md bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                            {viewCoach.role || '-'}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500">Status</p>
                          <span
                            className={`mt-1 inline-flex rounded-full border px-3 py-1 text-xs font-medium ${
                              viewCoach.status === 'Active'
                                ? 'border-green-200 bg-green-50 text-green-700'
                                : 'border-gray-200 bg-gray-50 text-gray-600'
                            }`}
                          >
                            {viewCoach.status || '-'}
                          </span>
                        </div>
                        <div className="md:col-span-2">
                          <p className="text-xs font-medium text-gray-500">Specialization</p>
                          <p className="mt-1 text-sm font-semibold text-black">
                            {viewCoach.specialization || '-'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Assigned Clients */}
                    <div className="rounded-lg border border-gray-200 bg-gray-50/50 p-5">
                      <h4 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-700">
                        <HiUserGroup className="h-4 w-4" />
                        Assigned Clients ({clientsForCoach.length})
                      </h4>
                      {clientsForCoach.length === 0 ? (
                        <div className="rounded-lg border border-gray-200 bg-white px-4 py-8 text-center">
                          <HiUserGroup className="mx-auto h-12 w-12 text-gray-300" />
                          <p className="mt-3 text-sm font-medium text-gray-600">
                            No clients assigned to this coach.
                          </p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                          {clientsForCoach.map(client => (
                            <div
                              key={client.id}
                              className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-all hover:border-gray-300 hover:shadow-md"
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-xs font-semibold text-white">
                                  {(client.firstname || client.email || 'C')[0].toUpperCase()}
                                </div>
                                <div className="flex-1 space-y-1">
                                  <p className="text-sm font-semibold text-black">
                                    {client.firstname} {client.lastname}
                                  </p>
                                  <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                    <HiEnvelope className="h-3.5 w-3.5" />
                                    <span className="truncate">{client.email}</span>
                                  </div>
                                  {client.organization && (
                                    <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                      <HiBuildingOffice2 className="h-3.5 w-3.5" />
                                      <span>{client.organization}</span>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2">
                                    {client.businessStage && (
                                      <span className="inline-flex rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                                        {client.businessStage}
                                      </span>
                                    )}
                                    {client.status && (
                                      <span
                                        className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${
                                          client.status === 'active'
                                            ? 'border-green-200 bg-green-50 text-green-700'
                                            : 'border-gray-200 bg-gray-50 text-gray-600'
                                        }`}
                                      >
                                        {client.status}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CoachesForms;
